import {
  BOOKING_STATUS_HINTS,
  BOOKING_STATUS_LABELS,
  formatIsoDate,
  formatPercent,
  formatPrice,
  formatSigned,
  type BookingStatus,
  type PositionView,
} from '@stockstop/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { useBookStopLoss, useDeletePosition, useUpdatePosition } from '../../api/queries.js';
import { BookingDialog } from './BookingDialog.js';
import { HistoryDialog } from './HistoryDialog.js';
import { PriceAxis } from './PriceAxis.js';
import { PriceSparkline } from './PriceSparkline.js';
import { useCardReorder, type CardReorder } from './useCardReorder.js';

/**
 * Das Portfolio als Karten.
 *
 * Jede Karte beantwortet zwei Fragen: Wo steht der Wert, und ist der Stop-Loss
 * beim Broker wirksam hinterlegt? Die zweite Frage bestimmt den Rahmen der Karte,
 * weil sie den Handlungsbedarf trägt — eine Position ohne gültige Order ist
 * ungeschützt, und das darf man nicht übersehen.
 */

interface PortfolioListProps {
  positions: PositionView[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

/** Grün bei Gewinn, rot bei Verlust — für Beträge und Prozentwerte gleichermaßen. */
const toneOf = (value: number): string => (value >= 0 ? 'tone-ok' : 'tone-danger');

/** Rahmenfarbe nach Dringlichkeit. */
const STATUS_MODIFIER: Record<BookingStatus, string> = {
  not_booked: 'is-alarm',
  expired: 'is-alarm',
  diverged: 'is-warn',
  active: '',
};

const PositionCard = ({
  position,
  isSelected,
  onSelect,
  onRequestDelete,
  onRequestBooking,
  onRevertToBooked,
  onShowHistory,
  isReverting,
  reorder,
}: {
  position: PositionView;
  isSelected: boolean;
  onSelect: () => void;
  onRequestDelete: () => void;
  onRequestBooking: () => void;
  onRevertToBooked: () => void;
  onShowHistory: () => void;
  isReverting: boolean;
  reorder: CardReorder;
}): React.JSX.Element => {
  const evaluation = position.stopLossEvaluation;
  const currency = position.instrument.currency;
  const status = position.bookingStatus;

  return (
    <li
      ref={reorder.registerCard(position)}
      className={[
        'position-card',
        STATUS_MODIFIER[status],
        isSelected ? 'is-selected' : '',
        reorder.draggingId === position.id ? 'is-dragging' : '',
        reorder.overId === position.id && reorder.draggingId !== position.id
          ? reorder.dropBefore
            ? 'is-drop-before'
            : 'is-drop-after'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      {...reorder.cardProps(position)}
    >
      {/*
        Der Griff zum Umsortieren. Gezogen wird er und nicht die Karte, damit sich
        in der Karte weiterhin Text markieren lässt.

        Mit der Tastatur bewegen Pfeil hoch und runter dieselbe Karte um einen
        Platz — natives Ziehen ist ohne Maus nicht erreichbar.
      */}
      <button
        type="button"
        className="position-card__grip"
        title="Zum Umsortieren ziehen, oder mit Pfeiltasten bewegen"
        aria-label={`${position.instrument.symbol} umsortieren`}
        onClick={(event) => event.stopPropagation()}
        {...reorder.handleProps(position)}
      >
        <svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true" focusable="false">
          <g fill="currentColor">
            <circle cx="2" cy="3" r="1.3" />
            <circle cx="8" cy="3" r="1.3" />
            <circle cx="2" cy="8" r="1.3" />
            <circle cx="8" cy="8" r="1.3" />
            <circle cx="2" cy="13" r="1.3" />
            <circle cx="8" cy="13" r="1.3" />
          </g>
        </svg>
      </button>

      <button
        type="button"
        className="position-card__close"
        title={`${position.instrument.symbol} aus dem Portfolio entfernen`}
        aria-label={`${position.instrument.symbol} entfernen`}
        onClick={(event) => {
          event.stopPropagation();
          onRequestDelete();
        }}
      >
        ×
      </button>

      <div className="position-card__head">
        {/*
          Der Name führt, das Kürzel steht darunter. Wer sein Depot durchsieht,
          denkt in Firmen — „ABEC.DE" muss man erst übersetzen, und bei vier
          Xetra-Kürzeln nebeneinander gelingt das nicht auf einen Blick.
        */}
        <span className="position-card__identity">
          <span className="position-card__name" title={position.instrument.name}>
            {position.instrument.name}
          </span>
          <span className="position-card__symbol">{position.instrument.symbol}</span>
        </span>
        {position.quantity !== null ? (
          <span className="position-card__holding">
            {position.quantity} St.
            {position.entryPrice !== null
              ? ` · Ø ${formatPrice(position.entryPrice, { currency })}`
              : ''}
          </span>
        ) : null}
      </div>

      {/*
        Der Kurs ist die größte Zahl der Karte. Zuvor standen acht Zahlen gleich
        laut nebeneinander — ohne Einstieg für den Blick liest sich das wie eine
        Tabelle, nicht wie eine Karte.
      */}
      <div className="position-card__price-row">
        <span className="position-card__price">
          {position.latestClose === null ? '—' : formatPrice(position.latestClose, { currency })}
        </span>
        {position.changeValue !== null || position.changePercent !== null ? (
          <span
            className={`position-card__change ${
              position.changePercent === null ? '' : toneOf(position.changePercent)
            }`}
            title="Veränderung zum vorherigen Schlusskurs"
          >
            {position.changeValue !== null
              ? formatSigned(position.changeValue, { currency, digits: 0 })
              : null}
            {position.changePercent !== null ? (
              <span className="position-card__change-percent">
                {formatPercent(position.changePercent)}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>

      {/*
        Verlauf und Preisachse zeigen bewusst Verschiedenes, damit keine Aussage
        zweimal dasteht: Die Linie setzt den Stop ins Verhältnis zu den letzten 60
        Handelstagen — hätte ihn die jüngste Schwankung ausgelöst? Die Achse setzt
        ihn ins Verhältnis zum Einstand, der außerhalb dieser 60 Tage liegt.
      */}
      <PriceSparkline
        closes={position.recentCloses}
        stopPrice={evaluation.stopPrice}
        label={`Kursverlauf ${position.instrument.symbol}, ${position.recentCloses.length} Handelstage`}
      />

      <div className="position-card__stop">
        <span className="position-card__stop-label">Stop</span>
        <span className="position-card__stop-price">
          {evaluation.stopPrice === null
            ? 'nicht gesetzt'
            : formatPrice(evaluation.stopPrice, { currency })}
        </span>
        {evaluation.triggered ? (
          <span className="position-card__cushion tone-danger">erreicht</span>
        ) : evaluation.distancePercent !== null ? (
          <span
            className="position-card__cushion"
            title="Wie weit der Kurs bis zum Stop fallen darf"
          >
            {formatPercent(evaluation.distancePercent, { showSign: false })} Puffer
          </span>
        ) : null}
      </div>

      {position.entryPrice !== null &&
      evaluation.stopPrice !== null &&
      position.latestClose !== null ? (
        <PriceAxis
          entryPrice={position.entryPrice}
          stopPrice={evaluation.stopPrice}
          currentPrice={position.latestClose}
          label={`Einstand ${formatPrice(position.entryPrice, { currency })}, Stop ${formatPrice(
            evaluation.stopPrice,
            { currency },
          )}, Kurs ${formatPrice(position.latestClose, { currency })}`}
        />
      ) : null}

      {/*
        Die vier Beträge, die der Bestand ausmacht. Jeder steht genau einmal: Der
        Stop-Kurs steht oben an der Linie, der Abstand daneben — hier unten geht es
        nur noch um Euro, nicht mehr um Kurse.
      */}
      <dl className="position-card__facts">
        <div className="fact">
          <dt>Wert</dt>
          <dd>
            {position.marketValue === null
              ? '—'
              : formatPrice(position.marketValue, { currency, digits: 0 })}
          </dd>
        </div>
        <div className="fact">
          <dt>beim Stop</dt>
          <dd>
            {position.stopLossValue === null
              ? '—'
              : formatPrice(position.stopLossValue, { currency, digits: 0 })}
          </dd>
        </div>
        <div className="fact">
          <dt>Einstand</dt>
          <dd>
            {position.costBasis === null
              ? '—'
              : formatPrice(position.costBasis, { currency, digits: 0 })}
          </dd>
        </div>
        <div className="fact" title="Ergebnis gegenüber dem Einstand, falls der Stop auslöst">
          <dt>Ergebnis</dt>
          <dd className={position.stopLossResult === null ? '' : toneOf(position.stopLossResult)}>
            {position.stopLossResult === null
              ? '—'
              : formatSigned(position.stopLossResult, { currency, digits: 0 })}
          </dd>
        </div>
      </dl>

      {/*
        Der ruhige Zustand bleibt ruhig: Liegt die Order beim Broker und stimmt mit
        dem Stop überein, genügt ein Punkt und eine Frist. Ein grüner Ausweis auf
        jeder Karte unterscheidet nichts und nähme nur das Gewicht weg, das die
        Zahlen brauchen. Laut wird die Zeile erst, wenn etwas zu tun ist.
      */}
      <div className="position-card__booking" title={BOOKING_STATUS_HINTS[status]}>
        {status === 'active' ? (
          <span className="position-card__state">
            <i className="position-card__dot" />
            {position.booking
              ? `eingebucht bis ${formatIsoDate(position.booking.validUntil)}`
              : BOOKING_STATUS_LABELS[status]}
          </span>
        ) : (
          <>
            <span className={`booking-badge booking-badge--${status}`}>
              {BOOKING_STATUS_LABELS[status]}
            </span>
            {position.booking ? (
              <span className="muted small position-card__booking-detail">
                {formatPrice(position.booking.stopPrice, { currency })} · bis{' '}
                {formatIsoDate(position.booking.validUntil)}
              </span>
            ) : null}
          </>
        )}

        {/*
          Die Knöpfe erscheinen nur, wo etwas zu tun ist. Bei `active` liegt die
          Order beim Broker und stimmt mit dem Stop überein — dann wäre ein Knopf
          eine Aufforderung ohne Anlass.

          Als eigene Gruppe, damit sie beieinander bleiben: Der Freiraum entsteht
          vor der Gruppe, nicht zwischen den Knöpfen.
        */}
        <div className="position-card__actions">
          {/* Der Verlauf ist immer erreichbar — auch bei stimmiger Order ist die
              Frage, wie es dahin kam, berechtigt. */}
          <button
            type="button"
            className="position-card__history"
            title="Verlauf der Stop-Loss-Entscheidungen"
            aria-label={`Verlauf von ${position.instrument.symbol}`}
            onClick={(event) => {
              event.stopPropagation();
              onShowHistory();
            }}
          >
            Verlauf
          </button>

          {status === 'diverged' ? (
            <button
              type="button"
              className="position-card__revert"
              disabled={isReverting}
              title="Den Stop-Loss auf den eingebuchten Kurs zurücksetzen und die Chart-Linie mitnehmen"
              onClick={(event) => {
                event.stopPropagation();
                onRevertToBooked();
              }}
            >
              {isReverting ? 'Setzt zurück …' : 'Zurücksetzen'}
            </button>
          ) : null}

          {status !== 'active' ? (
            <button
              type="button"
              className="button-primary"
              disabled={evaluation.stopPrice === null}
              onClick={(event) => {
                event.stopPropagation();
                onRequestBooking();
              }}
            >
              {status === 'diverged' ? 'Neu einbuchen' : 'Einbuchen'}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
};

export const PortfolioList = ({
  positions,
  selectedId,
  onSelect,
}: PortfolioListProps): React.JSX.Element => {
  const [pendingDelete, setPendingDelete] = useState<PositionView | null>(null);
  const [pendingBooking, setPendingBooking] = useState<PositionView | null>(null);
  const [historyFor, setHistoryFor] = useState<PositionView | null>(null);
  const deletePosition = useDeletePosition();
  const bookStopLoss = useBookStopLoss();
  const updatePosition = useUpdatePosition();
  const reorder = useCardReorder(positions);

  /**
   * Verwirft eine Änderung an der Stop-Loss-Linie.
   *
   * Der Gegenzug zum Einbuchen: Statt die Order beim Broker nachzuziehen, kehrt
   * der Stop auf den Kurs zurück, der dort bereits liegt. Die Chart-Linie folgt,
   * weil sie ihren Kurs aus der Position liest.
   */
  const revertToBooked = (position: PositionView): void => {
    if (!position.booking) return;
    updatePosition.mutate({
      id: position.id,
      input: { stopLossPrice: position.booking.stopPrice },
    });
  };

  if (positions.length === 0) {
    return <p className="muted">Noch keine Werte. Füge unten einen hinzu.</p>;
  }

  return (
    <>
      <ul className="position-list">
        {positions.map((position) => (
          <PositionCard
            key={position.id}
            position={position}
            isSelected={position.id === selectedId}
            onSelect={() => onSelect(position.id)}
            onRequestDelete={() => setPendingDelete(position)}
            onRequestBooking={() => setPendingBooking(position)}
            onRevertToBooked={() => revertToBooked(position)}
            onShowHistory={() => setHistoryFor(position)}
            isReverting={updatePosition.isPending && updatePosition.variables?.id === position.id}
            reorder={reorder}
          />
        ))}
      </ul>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Wert entfernen"
        confirmLabel="Entfernen"
        isPending={deletePosition.isPending}
        message={
          <>
            <p>
              Soll <strong>{pendingDelete?.instrument.symbol}</strong> wirklich aus dem Portfolio
              entfernt werden?
            </p>
            <p className="muted small">
              Die Chart-Zeichnungen dieses Wertes gehen dabei verloren. Die Kurshistorie bleibt
              erhalten.
            </p>
          </>
        }
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          deletePosition.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
        }}
      />

      <HistoryDialog position={historyFor} onClose={() => setHistoryFor(null)} />

      <BookingDialog
        position={pendingBooking}
        isPending={bookStopLoss.isPending}
        onCancel={() => setPendingBooking(null)}
        onConfirm={(validUntil) => {
          if (!pendingBooking) return;
          bookStopLoss.mutate(
            { id: pendingBooking.id, validUntil },
            { onSettled: () => setPendingBooking(null) },
          );
        }}
      />
    </>
  );
};
